import React, { useMemo, useState } from 'react';
import {
	Link,
	useLoaderData,
	useNavigate,
	useNavigation,
} from 'react-router-dom';
import { Avatar, Box, Button, MenuItem, Tab, TextField } from '@mui/material';
import { LoadingButton, TabContext, TabList } from '@mui/lab';
import { useImmer, useImmerReducer } from 'use-immer';
import { toast } from 'react-toastify';
import {
	BackgroundCheckRequests,
	FilterActionSchema,
	FilterStateSchema,
	applicationResultTypes,
	applicationStatusTypes,
	filterActionSchema,
	filterStateSchema,
	initialFilterState,
} from './types';
import { useCheckTypes, useFilterMutation } from '../../../hooks';
import { downloadPDF, getDate } from '../../../Common';
import { BackgroundCheckDrawer, OptionMenu } from '../../../components';
import {
	doUpdateBackgroundCheck,
	getBackgroundCheck,
} from '../../../apis/user.api';
import Loading from '../../../components/Loading';
import { getReport } from '../../../apis/user.api';
import clsx from 'clsx';

export type FromBackgroundCheck = {
	activeBackgroundCheck: true;
};

export type DrawerContent = {
	drawerData: DrawerData;
	email: string;
	date: Date;
	uuid: string;
};

export default function BackgroundCheck() {
	const navigate = useNavigate();
	const { state: isDownloadingPDF, location } = useNavigation();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [drawerContent, setDrawerContent] = useState<DrawerContent>({
		uuid: '',
		email: '',
		drawerData: [],
		date: new Date(),
	});
	const { data: types, isLoading } = useCheckTypes();
	const typesMap = useMemo(
		() => types?.reduce((acc, v) => ({ ...acc, [v.uuid]: v.name }), {}),
		[types]
	) as { [k: string]: string };
	const [tab, setTab] = useImmer<(typeof tabs)[number]>('ALL');
	const check = useLoaderData() as BackgroundCheckRequests;
	const [checks, setChecks] = useImmer<BackgroundCheckRequests>(() =>
		check.slice().reverse()
	);
	const doFilterMutation = useFilterMutation();
	const doResetMutation = useFilterMutation();
	const [state, dispatch] = useImmerReducer(
		(draftState = initialFilterState, action: FilterActionSchema) => {
			const result = filterActionSchema.safeParse(action);
			if (!result.success) throw new Error(result.error.message);
			const { type, payload } = result.data;
			switch (type) {
				case 'CHANGE_QUERY': {
					draftState.searchQuery = payload;
					break;
				}
				case 'SELECT_SCAN_TYPE': {
					draftState.applicationScanType = payload;
					break;
				}
				case 'SELECT_APPLICATION_STATUS_TYPE': {
					draftState.applicationStatusType = payload;
					break;
				}
				case 'SELECT_APPLICATION_RESULT_TYPE': {
					draftState.applicationResultType = payload;
					break;
				}
				case 'SELECT_IS_ACTIVE': {
					draftState.is_active = payload;
					break;
				}
				case 'SELECT_BACKGROUND_CHECK_STATUS': {
					draftState.backgroundCheckStatus = payload;
					break;
				}
				case 'RESET': {
					draftState = initialFilterState;
					break;
				}
				default:
					throw new Error(`Unknown action type:${type} `);
			}
			return draftState;
		},
		initialFilterState
	);

	if (
		(isDownloadingPDF === 'loading' &&
			location.pathname.includes('background-check')) ||
		isLoading
	)
		return <Loading />;

	async function refetchData() {
		const response = (await getBackgroundCheck({})) as {
			results: BackgroundCheckRequests;
		};
		const { results: checks } = response;
		if (tab === 'ALL') return setChecks(checks);
		setChecks(
			checks.filter(val => tab.toLowerCase().includes(val.status.toLowerCase()))
		);
	}

	async function handleTabChange(newTab: (typeof tabs)[number]) {
		setTab(newTab);
		const response = (await getBackgroundCheck({})) as {
			results: BackgroundCheckRequests;
		};
		const { results: checks } = response;
		if (newTab === 'ALL') return setChecks(checks);
		setChecks(
			checks.filter(val =>
				newTab.toLowerCase().includes(val.status.toLowerCase())
			)
		);
	}

	const {
		searchQuery,
		applicationScanType,
		applicationResultType,
		applicationStatusType,
	} = state;

	function handleSearchQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
		return dispatch({ type: 'CHANGE_QUERY', payload: e.target.value });
	}

	function handleEnterPress(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'Enter') e.preventDefault();
	}

	async function handleSearchSubmit() {
		const result = filterStateSchema.safeParse(state);
		if (!result.success) {
			return toast.error(result.error.message);
		}
		const { data } = result;
		const params = Object.fromEntries(
			Object.entries({
				score: data.applicationResultType,
				search: data.searchQuery,
				is_active: true,
				application_status: data.applicationStatusType,
				status: data.backgroundCheckStatus,
				scan_type: data.applicationScanType,
			}).filter(([_, v]) => v != null)
		);
		const response = await doFilterMutation.mutateAsync(params);
		if (Array.isArray(response)) {
			setChecks(response);
		}
	}

	async function handleReset() {
		dispatch({ type: 'RESET' });
		const response = await doResetMutation.mutateAsync(initialFilterState);
		if (Array.isArray(response)) {
			setChecks(response);
		}
	}

	return (
		<div
			className='main-panel'
			style={{ float: 'none', width: '100%' }}
		>
			<div
				className='content'
				style={{ padding: '0px', minHeight: 'auto' }}
			>
				<div className='container-fluid'>
					<div className='card-plain'>
						<div className='card-body '>
							<div className='row'>
								<div className='col-md-12'>
									<div className='card-plain bb10'>
										<div className='row justify-content-between'>
											<div className='col-md-4'>
												<h3>Background Check</h3>
											</div>
											<div className='nomobile mr-lg-3'>
												<Link
													className='btn btn-primary btn-lg'
													to='../add-new-request'
													state={
														{
															activeBackgroundCheck: true,
														} as FromBackgroundCheck
													}
												>
													Screen an applicant
												</Link>
												&nbsp;
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Box sx={{ width: '100%', typography: 'body1' }}>
						<TabContext value={tab}>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<TabList
									onChange={(_e, data) => handleTabChange(data)}
									aria-label='background check status tabs'
									textColor='secondary'
									indicatorColor='secondary'
									variant='scrollable'
									scrollButtons
									allowScrollButtonsMobile
								>
									<Tab
										label='All'
										value='ALL'
									/>
									<Tab
										label='Prospects'
										value='PROSPECTS'
										wrapped
									/>
									<Tab
										label='Employment Pending'
										value='EMPLOYMENT PENDING'
										wrapped
									/>
									<Tab
										label='Employees'
										value='EMPLOYEES'
										wrapped
									/>
									<Tab
										label='Archive'
										value='ARCHIVE'
										wrapped
									/>
								</TabList>
							</Box>
						</TabContext>
					</Box>

					<div className='row'>
						<div className='col-md-12 lrpad'>
							<div className='card mt_zero pad_zero'>
								<div className='search-box'>
									<div className='row'>
										<div className='col-md-8'>
											<form className='navbar-form'>
												<div className='input-group no-border'>
													<i className='material-icons sr_icon'>search</i>
													<input
														type='text'
														value={searchQuery ?? ''}
														onChange={handleSearchQueryChange}
														className='form-control'
														placeholder='Search by candidate name, email or report ID'
														onKeyDown={handleEnterPress}
													/>
												</div>
											</form>
										</div>
									</div>
								</div>
								<Box className='grid gap-4 px-3 grid-cols-1 lg:grid-cols-4'>
									<TextField
										select
										label='Search by scan type'
										variant='outlined'
										size='small'
										value={applicationScanType ?? ''}
										onChange={e => {
											dispatch({
												type: 'SELECT_SCAN_TYPE',
												payload: e.target.value,
											});
										}}
									>
										{!!types ? (
											types?.map(val => (
												<MenuItem
													key={val.uuid}
													value={val.uuid}
												>
													{val.name}
												</MenuItem>
											))
										) : (
											<MenuItem
												key={''}
												value={''}
											>
												{''}
											</MenuItem>
										)}
									</TextField>
									<TextField
										select
										label='Search by application status'
										variant='outlined'
										size='small'
										value={applicationStatusType ?? ''}
										onChange={e =>
											dispatch({
												type: 'SELECT_APPLICATION_STATUS_TYPE',
												payload: e.target
													.value as FilterStateSchema['applicationStatusType'],
											})
										}
									>
										{applicationStatusTypes.map(val => (
											<MenuItem
												key={val}
												value={val}
												className='capitalize'
											>
												{val}
											</MenuItem>
										))}
									</TextField>
									<TextField
										select
										label='Search by application result'
										size='small'
										variant='outlined'
										value={applicationResultType ?? ''}
										onChange={e =>
											dispatch({
												type: 'SELECT_APPLICATION_RESULT_TYPE',
												payload: e.target
													.value as FilterStateSchema['applicationResultType'],
											})
										}
									>
										{applicationResultTypes?.map(val => (
											<MenuItem
												key={val}
												value={val}
												className='capitalize'
											>
												{val}
											</MenuItem>
										))}
									</TextField>
									<Box className='grid grid-cols-2 gap-2'>
										<LoadingButton
											variant='contained'
											onClick={handleSearchSubmit}
											loading={doFilterMutation.isLoading}
										>
											Search
										</LoadingButton>
										<LoadingButton
											variant='outlined'
											onClick={handleReset}
											loading={doResetMutation.isLoading}
											color='secondary'
											disabled={
												JSON.stringify(state) ===
												JSON.stringify(initialFilterState)
											}
										>
											Reset
										</LoadingButton>
									</Box>
								</Box>

								<div
									className='showonmobile mt2 lrpad'
									style={{ display: 'none' }}
								>
									{checks.map(val => (
										<Box
											className='mb-3 pt-3 relative'
											key={val.uuid}
											onClick={() => navigate(`./${val.uuid}`)}
										>
											<table className='table mobile-data-table border-0'>
												<tbody style={{ borderLeft: '4px solid #250c77' }}>
													<tr>
														<td className='w30 font-bold'>Updated</td>
														<td>
															{val.webhook_hit_at
																? getDate(val.webhook_hit_at, 'mmddyyyy')
																: getDate(val.created_at, 'mmddyyyy')}
														</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>Email</td>
														<td>{val.email}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>First Name</td>
														<td>{val.firstName}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>Last Name</td>
														<td>{val.lastName}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>Scan Type</td>
														<td>{typesMap?.[val.scan_type]}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>
															Adjudication Status
														</td>
														<td>{val.status}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>
															Application Status
														</td>
														<td>{val.application_status}</td>
													</tr>
													<tr>
														<td className='w30 font-bold'>Score</td>
														<td>{val.score}</td>
													</tr>
												</tbody>
											</table>
											<Box className='absolute top-2.5 right-2.5'>
												<OptionMenu
													options={actions
														.slice()
														.filter(
															v =>
																!(
																	val.firstName === null && v === 'Download PDF'
																)
														)}
													handleMenuSelect={menuOption => {
														switch (menuOption) {
															case 'Move to Archive': {
																doUpdateBackgroundCheck({
																	uuid: val.uuid,
																	body: { status: 'Archive' },
																}).then(async () => await refetchData());
																break;
															}
															case 'Move to Prospects': {
																doUpdateBackgroundCheck({
																	uuid: val.uuid,
																	body: { status: 'Prospect' },
																}).then(async () => await refetchData());
																break;
															}
															case 'Move to Employees': {
																doUpdateBackgroundCheck({
																	uuid: val.uuid,
																	body: { status: 'Employee' },
																}).then(async () => await refetchData());
																break;
															}
															case 'Send Reminder Email': {
																alert('not implemented');
																break;
															}

															case 'Copy Application Link': {
																window.navigator.clipboard.writeText(
																	val.application_url
																);
																toast.success(
																	'Copied application link successfully!',
																	{
																		autoClose: 500,
																		hideProgressBar: true,
																		theme: 'dark',
																	}
																);
																break;
															}
															case 'Move to Employment Pending': {
																doUpdateBackgroundCheck({
																	uuid: val.uuid,
																	body: { status: 'Pending' },
																}).then(async () => await refetchData());
																break;
															}
															case 'Download PDF': {
																getReport(val.uuid).then(pdf => {
																	downloadPDF(
																		pdf,
																		val.firstName + val.lastName
																	);
																});
															}
														}
													}}
												/>
											</Box>
										</Box>
									))}
								</div>

								<div className='table-responsive hideonmobile'>
									<table className='table table-hover'>
										<thead>
											<tr>
												<th>#</th>
												<th className='w-[100px]'>Updated</th>
												<th>First Name</th>
												<th>Last Name</th>
												<th>Scan Type</th>
												<th>Adjudication Status</th>
												<th>Application Status</th>
												<th>Score</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{checks.map((val, i) => (
												<tr
													key={val.uuid}
													{...(val?.application_status?.toLowerCase() ===
														'complete' && {
														onClick: () => navigate(`./${val.uuid}`),
														className: 'hover:cursor-pointer',
													})}
												>
													<td>{i + 1}</td>
													<td>
														{val.webhook_hit_at
															? getDate(val.webhook_hit_at, 'mmddyyyy')
															: getDate(val.created_at, 'mmddyyyy')}
													</td>
													<td className='capitalize'>{val.firstName}</td>
													<td className='capitalize'>{val.lastName}</td>
													<td className='capitalize'>
														{typesMap?.[val.scan_type]}
													</td>
													<td className='capitalize'>{val.status}</td>
													<td className='capitalize'>
														<Button
															fullWidth
															variant='outlined'
															onClick={e => {
																e.stopPropagation();
																setIsDrawerOpen(true);
																setDrawerContent({
																	uuid: val.uuid,
																	email: val.email,
																	drawerData: val.check_executions,
																	date: new Date(val.updated_at),
																});
															}}
														>
															{val.application_status}
														</Button>
													</td>
													<td className='text-center'>
														<Avatar
															sx={{ width: 54, height: 54 }}
															className={clsx({
																'bg-green-400': val.score
																	.toLowerCase()
																	.includes('cleared'),
																'bg-yellow-400':
																	val.score.toLowerCase() === 'review',
																'text-slate-300 border-solid border-2 border-slate-300 bg-transparent':
																	val.score.toLowerCase().includes('none') &&
																	true,
															})}
														>
															{val.score.toLowerCase().includes('cleared')
																? 'CLR'
																: val.score === 'Review'
																? 'Rev'
																: 'None'}
														</Avatar>
													</td>
													<td>
														<OptionMenu
															options={actions
																.slice()
																.filter(
																	v =>
																		!(
																			val.firstName === null &&
																			v === 'Download PDF'
																		)
																)}
															handleMenuSelect={menuOption => {
																switch (menuOption) {
																	case 'Move to Archive': {
																		doUpdateBackgroundCheck({
																			uuid: val.uuid,
																			body: { status: 'Archive' },
																		}).then(async () => await refetchData());
																		break;
																	}
																	case 'Move to Prospects': {
																		doUpdateBackgroundCheck({
																			uuid: val.uuid,
																			body: { status: 'Prospect' },
																		}).then(async () => await refetchData());
																		break;
																	}
																	case 'Move to Employees': {
																		doUpdateBackgroundCheck({
																			uuid: val.uuid,
																			body: { status: 'Employees' },
																		}).then(async () => await refetchData());
																		break;
																	}
																	case 'Send Reminder Email': {
																		alert('not implemented');
																		break;
																	}

																	case 'Copy Application Link': {
																		window.navigator.clipboard.writeText(
																			val.application_url
																		);
																		toast.success(
																			'Copied application link successfully!',
																			{
																				autoClose: 500,
																				hideProgressBar: true,
																				theme: 'dark',
																			}
																		);
																		break;
																	}
																	case 'Move to Employment Pending': {
																		doUpdateBackgroundCheck({
																			uuid: val.uuid,
																			body: { status: 'Employment Pending' },
																		}).then(async () => await refetchData());
																		break;
																	}
																	case 'Download PDF': {
																		getReport(val.uuid).then(pdf => {
																			downloadPDF(
																				pdf,
																				val.firstName + val.lastName
																			);
																		});
																	}
																}
															}}
														/>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<BackgroundCheckDrawer
				isOpen={isDrawerOpen}
				closeDrawer={() => setIsDrawerOpen(false)}
				drawerContent={drawerContent}
			/>
		</div>
	);
}

const actions = [
	'Download PDF',
	'Move to Prospects',
	'Move to Employment Pending',
	'Move to Employees',
	'Move to Archive',
	'Copy Application Link',
] as const;

export const tabs = [
	'ALL',
	'PROSPECTS',
	'EMPLOYMENT PENDING',
	'EMPLOYEES',
	'ARCHIVE',
] as const;

export type WithoutNullableKeys<Type> = {
	[Key in keyof Type]-?: WithoutNullableKeys<NonNullable<Type[Key]>>;
};

const drawerContentData = [
	{
		id: 'check_exec_5yvWwKBOu71tACaqWUbzR6',
		status: 'WAITING_ON_CANDIDATE',
		check_name: 'criminal_record_check',
	},
	{
		id: 'check_exec_7zjpD0SOA59y4dTReWEpLH',
		status: 'WAITING_ON_CANDIDATE',
		check_name: 'identity_verification',
	},
];
export type DrawerData = typeof drawerContentData;
